import { useProductSetComposable } from '@/composables/products/ProductSetComposable';
import type { AppPageProps } from '@/types';
import { ConfigurableProductTypeEnum } from '@/types/generated';
import type { ConfigurableProductPageObject } from '@/types/product';
import { usePage } from '@inertiajs/vue3';
import _ from 'lodash';
import { defineStore } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

type Props = AppPageProps & {
    product: ConfigurableProductPageObject;
};

/**
 * Companion to ProductStore for set products. Not persisted, so a set page needs
 * none of the reset-and-restore choreography ProductStore has to do to survive
 * hydration.
 */
export const useProductSetStore = () => {
    const page = usePage<Props>();
    const { t } = useI18n();
    const sku = page.props.product.group_identifier;
    const storeName = _.snakeCase(page.props.store.store_group.name);
    const storeId = `product-set-store-${sku}-${storeName}`;

    return defineStore(storeId, () => {
        const configurableProduct = computed(() => page.props.product);

        const set = useProductSetComposable(
            configurableProduct.value.set,
            (number: number) => t('pages.product_detail_page.product_info.product_set.position_label', { number }),
            configurableProduct.value.type === ConfigurableProductTypeEnum.PANEL_SET,
        );

        const forceSelectionActive = computed(() => configurableProduct.value.force_size_selection && !set.hasUserSelected.value);

        return {
            ...set,
            forceSelectionActive,
        };
    })();
};
